import type { Doc } from '../../convex/_generated/dataModel';
import { formatModelLabel } from '../lib/models';
import type { ClientStage } from '../types';

interface Props {
  /** Etapa cliente (chunking/upload) */
  clientStage: ClientStage;
  clientMessage?: string;
  clientFraction?: number;
  clientChunkIndex?: number;
  clientTotalChunks?: number;

  /** Datos del meeting cuando ya existe en DB. Si null, todavía estamos en cliente. */
  meeting: Doc<'meetings'> | null;
  chunks: Doc<'chunks'>[];
  selectedTranscriptionModel?: string;

  errorMessage?: string;
}

interface Step {
  id: string;
  label: string;
  description: (props: Props) => string;
  status: (props: Props) => 'pending' | 'active' | 'done' | 'error';
}

const STEPS: Step[] = [
  {
    id: 'chunking',
    label: 'Preparación',
    description: (p) => {
      if (p.clientStage === 'chunking') return p.clientMessage ?? 'Preparando audio…';
      return 'Cortando audio en fragmentos si supera 24 MB';
    },
    status: (p) => {
      if (p.clientStage === 'chunking') return 'active';
      if (p.clientStage === 'idle') return 'pending';
      return 'done';
    },
  },
  {
    id: 'upload',
    label: 'Subida',
    description: (p) => {
      if (p.clientStage === 'uploading') {
        return p.clientTotalChunks && p.clientTotalChunks > 1
          ? `Fragmento ${p.clientChunkIndex}/${p.clientTotalChunks}`
          : 'Subiendo a Convex storage…';
      }
      return 'Cargando fragmentos a Convex storage';
    },
    status: (p) => {
      if (p.clientStage === 'uploading' || p.clientStage === 'creating-meeting') return 'active';
      if (
        p.clientStage === 'submitted' ||
        p.meeting !== null
      )
        return 'done';
      return 'pending';
    },
  },
  {
    id: 'transcribing',
    label: 'Transcripción',
    description: (p) => {
      const model = formatModelLabel(
        p.meeting?.transcriptionModel ?? p.selectedTranscriptionModel ?? 'openai/whisper-large-v3'
      );
      if (!p.meeting) return `${model} procesará cada fragmento`;
      const done = p.chunks.filter((c) => c.status === 'done').length;
      const total = p.chunks.length;
      if (p.meeting.status === 'transcribing') {
        return `${model} · ${done}/${total} fragmentos`;
      }
      if (p.meeting.status === 'summarizing' || p.meeting.status === 'done') {
        return `${total}/${total} fragmentos transcritos`;
      }
      return `${model} procesará cada fragmento`;
    },
    status: (p) => {
      if (!p.meeting) return 'pending';
      if (p.meeting.status === 'error' && p.meeting.chunksCompleted === 0) return 'error';
      if (p.meeting.status === 'transcribing') return 'active';
      if (
        p.meeting.status === 'summarizing' ||
        p.meeting.status === 'done'
      )
        return 'done';
      return 'pending';
    },
  },
  {
    id: 'summarizing',
    label: 'Resumen',
    description: (p) => {
      if (!p.meeting) return 'Extracción de puntos clave, decisiones, action items';
      return `${formatModelLabel(p.meeting.summaryModel)} extrayendo estructura`;
    },
    status: (p) => {
      if (!p.meeting) return 'pending';
      if (p.meeting.status === 'error' && p.meeting.chunksCompleted > 0) return 'error';
      if (p.meeting.status === 'summarizing') return 'active';
      if (p.meeting.status === 'done') return 'done';
      return 'pending';
    },
  },
];

export function MeetingProgress(props: Props) {
  const { clientStage, meeting, errorMessage } = props;

  // Solo mostramos cuando hay algo activo
  if (clientStage === 'idle' && !meeting) return null;
  // Si está done sin error, ocultamos (el detail panel se encarga)
  if (meeting && meeting.status === 'done') return null;

  const isError = clientStage === 'error' || meeting?.status === 'error';
  const finalError = errorMessage ?? meeting?.error;

  return (
    <section className="reveal-up" style={{ animationDelay: '60ms' }}>
      <p className="label-mono mb-4">
        <span className="text-[var(--color-amber-mid)]">[03]</span> · {isError ? 'process.fault' : 'process.running()'}
        <span className="text-[var(--color-asphalt-300)]"> // streaming</span>
      </p>

      <div className="term-box p-6 sm:p-8 relative z-10">
        <ol className="space-y-4">
          {STEPS.map((s, idx) => {
            const status = s.status(props);
            return (
              <li key={s.id} className="flex items-start gap-4">
                <div className="flex flex-col items-center pt-0.5">
                  <StepMarker status={status} idx={idx + 1} />
                  {idx < STEPS.length - 1 && (
                    <div
                      className={[
                        'w-px h-8 mt-1',
                        status === 'done'
                          ? 'bg-[var(--color-amber-mid)]'
                          : 'bg-[var(--color-asphalt-300)]',
                      ].join(' ')}
                    />
                  )}
                </div>
                <div className="flex-1 pb-2">
                  <div className="flex items-baseline justify-between gap-3 flex-wrap">
                    <p
                      className={[
                        'font-mono text-xs uppercase tracking-[0.14em]',
                        status === 'active'
                          ? 'text-[var(--color-amber-deep)] pulse-soft'
                          : status === 'done'
                            ? 'text-[var(--color-asphalt)]'
                            : status === 'error'
                              ? 'text-[var(--color-burgundy)]'
                              : 'text-[var(--color-asphalt-400)]',
                      ].join(' ')}
                    >
                      {s.label}
                    </p>
                    {status === 'done' && (
                      <p className="font-mono text-[10px] text-[var(--color-asphalt-400)]">✓ Completo</p>
                    )}
                    {status === 'error' && (
                      <p className="font-mono text-[10px] text-[var(--color-burgundy)]">✗ Falló</p>
                    )}
                  </div>
                  <p
                    className={[
                      'text-sm mt-0.5',
                      status === 'active'
                        ? 'text-[var(--color-asphalt)]'
                        : 'text-[var(--color-asphalt-400)]',
                    ].join(' ')}
                  >
                    {s.description(props)}
                  </p>

                  {/* Barra de progreso para fases con fracción conocida */}
                  {status === 'active' && s.id === 'upload' && props.clientFraction !== undefined && (
                    <div className="progress-track mt-3 max-w-md">
                      <div
                        className="progress-fill"
                        style={{ width: `${Math.round(props.clientFraction * 100)}%` }}
                      />
                    </div>
                  )}

                  {status === 'active' && s.id === 'transcribing' && props.meeting && (
                    <div className="progress-track mt-3 max-w-md">
                      <div
                        className="progress-fill"
                        style={{
                          width: `${
                            props.meeting.chunkCount === 0
                              ? 0
                              : Math.round(
                                  (props.meeting.chunksCompleted / props.meeting.chunkCount) * 100
                                )
                          }%`,
                        }}
                      />
                    </div>
                  )}

                  {status === 'active' && s.id === 'summarizing' && (
                    <div className="progress-track mt-3 max-w-md">
                      <div className="progress-indeterminate" />
                    </div>
                  )}

                  {status === 'active' && s.id === 'chunking' && (
                    <div className="progress-track mt-3 max-w-md">
                      <div className="progress-indeterminate" />
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>

        {isError && finalError && (
          <div className="mt-6 pt-6 border-t border-[var(--color-asphalt-300)]">
            <p className="label-mono text-[var(--color-burgundy)] mb-2">stderr ▸ stack_trace</p>
            <p className="font-mono text-xs text-[var(--color-burgundy)] leading-relaxed">
              {finalError}
            </p>
          </div>
        )}
      </div>
    </section>
  );
}

function StepMarker({ status, idx }: { status: 'pending' | 'active' | 'done' | 'error'; idx: number }) {
  if (status === 'active') return <div className="spinner-tech" />;
  if (status === 'done') {
    return (
      <div className="w-3.5 h-3.5 rounded-full bg-[var(--color-asphalt)] flex items-center justify-center" style={{ boxShadow: 'var(--glow-green)' }}>
        <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
          <path
            d="M1.5 4 L3.5 6 L6.5 2"
            stroke="#020503"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    );
  }
  if (status === 'error') {
    return (
      <div className="w-3.5 h-3.5 rounded-full bg-[var(--color-burgundy)] flex items-center justify-center">
        <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
          <path
            d="M2 2 L6 6 M6 2 L2 6"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </div>
    );
  }
  return (
    <div className="w-3.5 h-3.5 rounded-full border border-[var(--color-asphalt-300)] bg-[var(--color-bone)] flex items-center justify-center">
      <span className="font-mono text-[8px] text-[var(--color-asphalt-400)]">{idx}</span>
    </div>
  );
}
